import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import {
  BUILD_ITEM_STATUSES,
  BUILD_ITEM_STATUS_LABEL,
  buildTypePillClass,
  buildStatusPillClass,
  initials,
  DELEGATING_ROLE_BY_TYPE,
} from '../../lib/workbench';
import {
  routeBuildItem,
  completeAssignmentTask,
  completeWorkTask,
  fetchOpenBuildItemTasks,
} from '../../lib/tasks';

function formatDue(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function isOverdue(dueDate, status) {
  if (!dueDate || status === 'done') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today;
}

export default function BuildItemTracker() {
  const { project, programme } = useOutletContext();
  const { canWrite } = useWorkspaceAccess();
  const [searchParams] = useSearchParams();
  const focusItemId = searchParams.get('focusItem');
  const orgId = programme?.organisation_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [routingByType, setRoutingByType] = useState(new Map());
  const [profiles, setProfiles] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const deliverablesPromise = sb
        .from('deliverables')
        .select('id, ref, title, lane, due_date, type_id, deliverable_types(name)')
        .eq('project_id', project.id)
        .eq('kind', 'build_item')
        .order('order_index')
        .order('ref');

      const profsPromise = sb
        .from('profiles')
        .select('id, full_name')
        .order('full_name');

      const routingPromise = orgId
        ? sb
          .from('artefact_role_routing')
          .select('build_item_type, functional_roles(full_name)')
          .eq('organisation_id', orgId)
          .eq('source_artefact_type', 'Build item')
          .eq('task_type_to_create', 'assignment')
        : Promise.resolve({ data: [] });

      const [
        { data: deliverables, error },
        { data: profs },
        { data: routingRows },
        taskRows,
      ] = await Promise.all([
        deliverablesPromise,
        profsPromise,
        routingPromise,
        fetchOpenBuildItemTasks(project.id),
      ]);

      if (error) throw error;

      const ids = (deliverables || []).map((d) => d.id);
      let assignRows = [];
      if (ids.length) {
        const { data: a } = await sb
          .from('deliverable_assignments')
          .select('deliverable_id, profile_id, is_lead, profiles(full_name)')
          .in('deliverable_id', ids);
        assignRows = a || [];
      }

      const routeMap = new Map(
        (routingRows || []).map((r) => [r.build_item_type, r.functional_roles?.full_name])
      );

      setItems(deliverables || []);
      setAssignments(assignRows);
      setOpenTasks(taskRows);
      setRoutingByType(routeMap);
      setProfiles(profs || []);
    } catch (e) {
      console.error('[BuildItemTracker] load failed', e);
      showToast('Failed to load build items');
    } finally {
      setLoading(false);
    }
  }, [project?.id, orgId]);

  useEffect(() => { load(); }, [load]);

  const ownerByDeliverable = useMemo(() => {
    const map = new Map();
    for (const a of assignments) {
      map.set(a.deliverable_id, a);
    }
    return map;
  }, [assignments]);

  const assignmentTaskByDeliverable = useMemo(() => {
    const map = new Map();
    for (const t of openTasks) {
      if (t.task_type === 'assignment') map.set(t.source_artefact_id, t);
    }
    return map;
  }, [openTasks]);

  const workTaskByDeliverable = useMemo(() => {
    const map = new Map();
    for (const t of openTasks) {
      if (t.task_type === 'work') map.set(t.source_artefact_id, t);
    }
    return map;
  }, [openTasks]);

  const delegatingRoleFor = useCallback((typeName) => (
    routingByType.get(typeName)
    || DELEGATING_ROLE_BY_TYPE[typeName]
    || 'Lead'
  ), [routingByType]);

  const typeOptions = useMemo(() => {
    const set = new Set(items.map((i) => i.deliverable_types?.name).filter(Boolean));
    return [...set].sort();
  }, [items]);

  const visibleItems = useMemo(() => items.filter((item) => {
    const status = item.lane || 'not_started';
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    const typeName = item.deliverable_types?.name || '';
    if (typeFilter && typeName !== typeFilter) return false;
    return true;
  }), [items, statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const counts = { not_started: 0, assignment_pending: 0, in_progress: 0, done: 0 };
    for (const item of items) {
      const s = item.lane || 'not_started';
      if (counts[s] !== undefined) counts[s] += 1;
    }
    return {
      total: items.length,
      not_started: counts.not_started,
      in_progress: counts.in_progress,
      done: counts.done,
      assignment_pending: counts.assignment_pending,
    };
  }, [items]);

  function openAssignModal(item) {
    setModalItem(item);
    setSelectedMember(null);
  }

  async function confirmAssignment() {
    if (!modalItem || !selectedMember) return;
    const assignmentTask = assignmentTaskByDeliverable.get(modalItem.id);
    if (!assignmentTask) {
      showToast('No open assignment task — route to lead first');
      return;
    }

    setSaving(true);
    try {
      await completeAssignmentTask(assignmentTask.id, selectedMember);
      setModalItem(null);
      showToast('Assigned — Work task created', 'success');
      await load();
    } catch (e) {
      console.error('[BuildItemTracker] assign failed', e);
      showToast(e.message || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  }

  async function handleRouteToLead(item) {
    setSaving(true);
    try {
      await routeBuildItem(item.id);
      showToast('Assignment task created', 'success');
      await load();
    } catch (e) {
      console.error('[BuildItemTracker] route failed', e);
      showToast(e.message || 'Failed to route build item');
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkDone(item) {
    const workTask = workTaskByDeliverable.get(item.id);
    if (!workTask) {
      showToast('No open work task for this build item');
      return;
    }

    setSaving(true);
    try {
      await completeWorkTask(workTask.id);
      showToast('Build item marked done', 'success');
      await load();
    } catch (e) {
      console.error('[BuildItemTracker] complete work failed', e);
      showToast(e.message || 'Failed to mark done');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading build item tracker…" />;

  const modalTypeName = modalItem?.deliverable_types?.name || '';
  const modalRole = delegatingRoleFor(modalTypeName);

  return (
    <div className="wb-tracker-shell">
      <div className="wb-review-head">
        <div>
          <div className="wb-review-title">Build Item Tracker</div>
          <div className="wb-review-sub">
            <b>{programme?.name}</b>
            {' · '}
            {project?.name}
            {' · all build item types, unified'}
          </div>
        </div>
        <div className="wb-run-progress">
          <div className="wb-run-stat">
            <div className="wb-run-stat-num">{stats.total}</div>
            <div className="wb-run-stat-lbl">Total</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num muted">{stats.not_started}</div>
            <div className="wb-run-stat-lbl">Not started</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num progress">{stats.in_progress + stats.assignment_pending}</div>
            <div className="wb-run-stat-lbl">In progress</div>
          </div>
          <div className="wb-run-stat">
            <div className="wb-run-stat-num confirmed">{stats.done}</div>
            <div className="wb-run-stat-lbl">Done</div>
          </div>
        </div>
      </div>

      <div className="wb-toolbar">
        <div className="wb-toolbar-group">
          <span className="wb-toolbar-label">Status</span>
          <button
            type="button"
            className={`wb-filter-pill ${statusFilter === 'all' ? 'on' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          {BUILD_ITEM_STATUSES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`wb-filter-pill ${statusFilter === s.key ? 'on' : ''}`}
              onClick={() => setStatusFilter(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="wb-toolbar-group">
          <span className="wb-toolbar-label">Type</span>
          <select
            className="wb-type-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wb-table-wrap">
        <table className="wb-tracker-table">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Owner</th>
              <th>Due</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item) => {
              const status = item.lane || 'not_started';
              const typeName = item.deliverable_types?.name || '—';
              const owner = ownerByDeliverable.get(item.id);
              const ownerName = owner?.profiles?.full_name;
              const assignmentTask = assignmentTaskByDeliverable.get(item.id);
              const workTask = workTaskByDeliverable.get(item.id);
              const pendingRole = delegatingRoleFor(typeName);

              return (
                <tr key={item.id} className={focusItemId === item.id ? 'task-focus' : undefined}>
                  <td><span className="wb-bi-ref">{item.ref || '—'}</span></td>
                  <td>{item.title}</td>
                  <td>
                    <span className={`wb-type-pill ${buildTypePillClass(typeName)}`}>
                      {typeName}
                    </span>
                  </td>
                  <td>
                    <span className={`wb-status-pill ${buildStatusPillClass(status)}`}>
                      {BUILD_ITEM_STATUS_LABEL[status] || status}
                    </span>
                  </td>
                  <td>
                    {status === 'assignment_pending' ? (
                      <div className="wb-owner-cell">
                        <div className="wb-owner-av unassigned">?</div>
                        <span className="wb-owner-muted">
                          Pending — {pendingRole}
                          {assignmentTask?.profiles?.full_name && (
                            <> ({assignmentTask.profiles.full_name})</>
                          )}
                        </span>
                      </div>
                    ) : ownerName ? (
                      <div className="wb-owner-cell">
                        <div className="wb-owner-av">{initials(ownerName)}</div>
                        {ownerName}
                        {owner?.is_lead && <span className="wb-owner-lead-tag">(lead)</span>}
                      </div>
                    ) : (
                      <div className="wb-owner-cell">
                        <div className="wb-owner-av unassigned">—</div>
                        <span className="wb-owner-muted">Unassigned</span>
                      </div>
                    )}
                  </td>
                  <td className={isOverdue(item.due_date, status) ? 'wb-due-overdue' : ''}>
                    {formatDue(item.due_date)}
                  </td>
                  <td>
                    {canWrite && status === 'assignment_pending' && assignmentTask && (
                      <button
                        type="button"
                        className="wb-row-action-btn primary"
                        disabled={saving}
                        onClick={() => openAssignModal(item)}
                      >
                        Resolve assignment
                      </button>
                    )}
                    {canWrite && status === 'assignment_pending' && !assignmentTask && (
                      <button
                        type="button"
                        className="wb-row-action-btn"
                        disabled={saving}
                        onClick={() => handleRouteToLead(item)}
                      >
                        Create assignment task
                      </button>
                    )}
                    {canWrite && status === 'not_started' && (
                      <button
                        type="button"
                        className="wb-row-action-btn"
                        disabled={saving}
                        onClick={() => handleRouteToLead(item)}
                      >
                        Route to lead
                      </button>
                    )}
                    {canWrite && status === 'in_progress' && workTask && (
                      <button
                        type="button"
                        className="wb-row-action-btn"
                        disabled={saving}
                        onClick={() => handleMarkDone(item)}
                      >
                        Mark done
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!visibleItems.length && (
              <tr>
                <td colSpan={7} className="wb-table-empty">
                  {items.length
                    ? 'No build items match the current filters.'
                    : 'No build items yet. Confirm disposition links to seed the tracker.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modalItem && (
        <Modal
          title="Pick a team member"
          onClose={() => setModalItem(null)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="wb-modal-btn" onClick={() => setModalItem(null)}>Cancel</button>
              <button
                type="button"
                className="wb-modal-btn primary"
                disabled={!selectedMember || saving}
                onClick={confirmAssignment}
              >
                Assign &amp; create Work task
              </button>
            </>
          )}
        >
          <div className="wb-modal-eyebrow">Assignment task</div>
          <div className="wb-routing-trace">
            <b>{modalItem.ref}</b> — {modalItem.title}
            <br />
            Routed via <b>{modalRole}</b> (delegating role per build item type).
          </div>
          <div className="wb-modal-label">Team members on project</div>
          {profiles.slice(0, 8).map((p) => (
            <button
              key={p.id}
              type="button"
              className={`wb-team-member-option ${selectedMember === p.id ? 'selected' : ''}`}
              onClick={() => setSelectedMember(p.id)}
            >
              <div className="wb-tm-av">{initials(p.full_name)}</div>
              <span className="wb-tm-name">{p.full_name}</span>
            </button>
          ))}
        </Modal>
      )}

      {toast && (
        <div className={`toast ${toast.type === 'success' ? 'toast-success' : 'toast-error'}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
